import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDateTime } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AttendanceWithCamera({ user, onLogout }) {
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [history, setHistory] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [stream, setStream] = useState(null);
  const [checkInOut, setCheckInOut] = useState('in'); // 'in' or 'out'
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchTodayAttendance();
    fetchHistory();
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    // Cleanup camera stream on unmount
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayAttendance(response.data);
    } catch (error) {
      console.error('Failed to fetch today attendance:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setLocation(loc);
          resolve(loc);
        },
        (error) => reject(error)
      );
    });
  };

  const startCamera = async (type) => {
    setCheckInOut(type);
    setShowCamera(true);
    setCapturedPhoto(null);
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      alert('Camera access denied. Please allow camera permissions.');
      console.error('Camera error:', error);
      setShowCamera(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photoDataUrl);
      
      // Stop camera stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera(checkInOut);
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
    setCapturedPhoto(null);
    setStream(null);
  };

  const submitAttendance = async () => {
    if (!capturedPhoto) {
      alert('Please capture your photo first!');
      return;
    }

    setLoading(true);
    try {
      const loc = await getLocation();
      const token = localStorage.getItem('token');
      
      // Convert base64 to blob
      const blob = await fetch(capturedPhoto).then(r => r.blob());
      const formData = new FormData();
      formData.append('photo', blob, 'attendance.jpg');
      formData.append('latitude', loc.latitude);
      formData.append('longitude', loc.longitude);
      formData.append('address', 'Location');

      const endpoint = checkInOut === 'in' ? 'checkin' : 'checkout';
      
      await axios.post(
        `${API}/attendance/${endpoint}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      alert(`${checkInOut === 'in' ? 'Checked in' : 'Checked out'} successfully!`);
      closeCamera();
      fetchTodayAttendance();
      fetchHistory();
    } catch (error) {
      alert(error.response?.data?.detail || `Check-${checkInOut} failed`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6" style={{color: '#2b3380'}}>Attendance</h1>
        
        {/* Attendance Module Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Attendance Overview"
            icon="📅"
            bgColor="#4caf50"
            stats={[
              {
                label: 'Present Today',
                value: dashboardStats.present_today || 0,
                subtitle: `${dashboardStats.attendance_percentage}% rate`,
                color: '#4caf50'
              },
              {
                label: 'Absent Today',
                value: dashboardStats.absent_today || 0,
                subtitle: 'Needs attention',
                color: '#f44336'
              },
              {
                label: 'On Leave',
                value: dashboardStats.on_leave_today || 0,
                subtitle: 'Approved leaves',
                color: '#ff9800'
              },
              {
                label: 'Total Shifts',
                value: dashboardStats.total_shifts || 0,
                subtitle: 'Active shifts',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Camera Modal - Full Screen on Mobile */}
        {showCamera && (
          <div className="fixed inset-0 bg-black z-50 flex flex-col">
            {/* Header */}
            <div className="bg-gray-900 text-white p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold">
                {checkInOut === 'in' ? '📸 Check In Photo' : '📸 Check Out Photo'}
              </h2>
              <button 
                onClick={closeCamera}
                className="text-white text-2xl font-bold hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            {/* Camera/Photo Area */}
            <div className="flex-1 flex items-center justify-center bg-black relative overflow-hidden">
              {!capturedPhoto ? (
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <img 
                  src={capturedPhoto} 
                  alt="Captured" 
                  className="w-full h-full object-contain"
                />
              )}
              
              {/* Overlay guide */}
              {!capturedPhoto && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="border-4 border-white border-dashed rounded-full w-64 h-64 md:w-80 md:h-80 opacity-50"></div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="bg-gray-900 p-4 md:p-6">
              <div className="max-w-md mx-auto">
                {!capturedPhoto ? (
                  <button
                    onClick={capturePhoto}
                    className="w-full py-4 md:py-5 bg-white text-gray-900 rounded-full font-bold text-lg hover:bg-gray-200 transition flex items-center justify-center gap-2"
                  >
                    📷 Capture Photo
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={retakePhoto}
                      className="flex-1 py-4 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition"
                    >
                      🔄 Retake
                    </button>
                    <button
                      onClick={submitAttendance}
                      disabled={loading}
                      className="flex-1 py-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:bg-gray-600"
                    >
                      {loading ? '⏳ Submitting...' : '✅ Confirm'}
                    </button>
                  </div>
                )}
              </div>
              <p className="text-center text-white text-sm mt-3 opacity-75">
                {!capturedPhoto ? 'Position your face within the circle' : 'Review your photo before submitting'}
              </p>
            </div>

            {/* Hidden canvas for photo capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Today's Status Card */}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold mb-4" style={{color: '#2b3380'}}>Today's Status</h2>
            
            {todayAttendance?.checked_in ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">✅</span>
                    <div>
                      <p className="font-semibold text-green-800">Checked In</p>
                      <p className="text-sm text-green-600">
                        {formatDateTime(todayAttendance.check_in_time)}
                      </p>
                    </div>
                  </div>
                  {todayAttendance.check_in_photo && (
                    <img 
                      src={todayAttendance.check_in_photo} 
                      alt="Check-in" 
                      className="w-20 h-20 rounded-lg object-cover border-2 border-green-300 mt-2"
                    />
                  )}
                </div>

                {todayAttendance.checked_out ? (
                  <div className="bg-[#2b338015] border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">🏁</span>
                      <div>
                        <p className="font-semibold text-[#2b3380]">Checked Out</p>
                        <p className="text-sm text-[#2b3380]">
                          {formatDateTime(todayAttendance.check_out_time)}
                        </p>
                      </div>
                    </div>
                    {todayAttendance.check_out_photo && (
                      <img 
                        src={todayAttendance.check_out_photo} 
                        alt="Check-out" 
                        className="w-20 h-20 rounded-lg object-cover border-2 border-blue-300 mt-2"
                      />
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => startCamera('out')}
                    disabled={loading}
                    className="w-full py-4 rounded-lg text-white font-medium text-lg hover:opacity-90 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
                    style={{backgroundColor: '#f44336'}}
                  >
                    📸 Check Out
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🌅</div>
                <p className="text-gray-600 mb-6">Start your day!</p>
                <button
                  onClick={() => startCamera('in')}
                  disabled={loading}
                  className="w-full py-4 rounded-lg text-white font-medium text-lg hover:opacity-90 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
                  style={{backgroundColor: '#4caf50'}}
                >
                  📸 Check In
                </button>
              </div>
            )}
          </div>

          {/* Location Info Card */}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold mb-4" style={{color: '#2b3380'}}>Location Info</h2>
            
            {location ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📍</span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Current Location</p>
                    <p className="text-sm text-gray-600">
                      Lat: {location.latitude.toFixed(6)}<br/>
                      Lng: {location.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>
                <div className="bg-[#2b338015] border border-blue-200 rounded-lg p-3 text-sm text-[#2b3380]">
                  ℹ️ Your location is used to verify attendance from authorized premises
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-5xl mb-3">🌍</div>
                <p className="text-gray-600 text-sm">Location will be captured during check-in/out</p>
              </div>
            )}

            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-xl">📷</span>
                <span className="text-gray-700">Photo verification enabled</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-xl">📍</span>
                <span className="text-gray-700">Geo-location tracking</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-xl">⏰</span>
                <span className="text-gray-700">Real-time timestamp</span>
              </div>
            </div>
          </div>
        </div>

        {/* Attendance History */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold mb-4" style={{color: '#2b3380'}}>Recent Attendance History</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check In</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check Out</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photos</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.slice(0, 10).map((record) => (
                  <tr key={record.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">{record.date}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {record.check_in_time ? formatDateTime(record.check_in_time) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {record.check_out_time ? formatDateTime(record.check_out_time) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        record.status === 'Present' ? 'bg-green-100 text-green-800' :
                        record.status === 'Absent' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        {record.check_in_photo && (
                          <img 
                            src={record.check_in_photo} 
                            alt="In" 
                            className="w-10 h-10 rounded object-cover border cursor-pointer hover:scale-110 transition"
                            title="Check-in photo"
                          />
                        )}
                        {record.check_out_photo && (
                          <img 
                            src={record.check_out_photo} 
                            alt="Out" 
                            className="w-10 h-10 rounded object-cover border cursor-pointer hover:scale-110 transition"
                            title="Check-out photo"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AttendanceWithCamera;
